import { put, select, takeEvery, apply, call } from "redux-saga/effects";
import { assetTabsSlice, DEFAULT_ASSET } from "./assetTabsReducer";
import { PayloadAction } from "@reduxjs/toolkit";
import { IAssetTab } from "../../components/AssetTabs";
import appHistory from "../../app/appHistory";
import jsonParse from "../../utils/jsonParse";
import _ from "lodash";
import { STORAGE_KEY_ACTIVE_TAB, STORAGE_KEY_TABS } from "./AssetTabsConstants";
import { getUserUidSelector } from "../../app/appSelectors";

function* setActiveTabSagaWorker({ payload }: PayloadAction<IAssetTab>) {
    yield apply(localStorage, 'setItem', [STORAGE_KEY_ACTIVE_TAB, JSON.stringify(payload)]);
    yield put(assetTabsSlice.actions.setActiveTabAction(payload));
    const location = yield select(state => state.router.location);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('symbol', payload.assetSymbol);
    appHistory.replace({
        search: searchParams.toString(),
        pathname: location.pathname,
        state: location.state,
    });
}

function* getUserStorageKeySaga() {
    const userId = yield select(getUserUidSelector);
    return `${userId}_${STORAGE_KEY_TABS}`;
}

function* addTab(tab: IAssetTab) {
    const storageTabs: IAssetTab[] = yield select(state => state.assetTabs.tabs);
    const userStorageKey = yield getUserStorageKeySaga();
    yield apply(localStorage, 'setItem', [userStorageKey, JSON.stringify([...storageTabs, tab])]);
    yield put(assetTabsSlice.actions.addTabAction(tab));
    yield put(assetTabsSlice.actions.initActiveTabAction(tab));
}

function* deleteTabSagaWorker({ payload }: PayloadAction<string>) {
    const storageTabs: IAssetTab[] = yield select(state => state.assetTabs.tabs);
    const updatedTabs = storageTabs.filter(tab => tab.id !== payload);
    const userStorageKey = yield getUserStorageKeySaga();
    yield apply(localStorage, 'setItem', [userStorageKey, JSON.stringify(updatedTabs)]);
    yield put(assetTabsSlice.actions.deleteTabAction(payload));
    const activeTab = yield select(state => state.assetTabs.activeTab);
    if (activeTab && activeTab.id === payload) {
        yield put(assetTabsSlice.actions.initActiveTabAction(updatedTabs[updatedTabs.length - 1]));
    }
}

function* handleAddTabSagaWorker({ payload }: PayloadAction<string>) {
    yield call(handleAddTabSaga, payload);
}

function* handleAddTabSaga(assetSymbol: string) {
    const assetTabs: IAssetTab[] = yield select(state => state.assetTabs.tabs);
    const tab = assetTabs.find(tab => tab.assetSymbol === assetSymbol);
    if (tab) {
        yield put(assetTabsSlice.actions.initActiveTabAction(tab));
    } else {
        yield call(addTab, {
            assetSymbol,
            id: Date.now().toString()
        });
    }
}

function* initAssetTabsWorker() {
    const userStorageKey = yield getUserStorageKeySaga();
    const savedTabsJson: string = yield apply(localStorage, 'getItem', [userStorageKey]);
    let assetTabs: IAssetTab[];
    try {
        assetTabs = (yield apply(JSON, 'parse', [savedTabsJson])) || [DEFAULT_ASSET];
    } catch (error) {
        assetTabs = [DEFAULT_ASSET];
    }
    yield put(assetTabsSlice.actions.setTabsAction(assetTabs));
    const location = yield select(state => state.router.location);
    const searchParams = new URLSearchParams(location.search);
    const symbolInSearch = yield apply(searchParams, 'get', ['symbol']);
    const assetInStorage: string | null = yield apply(localStorage, 'getItem', [STORAGE_KEY_ACTIVE_TAB]);
    const currentSymbol = symbolInSearch || _.get(jsonParse(assetInStorage) as IAssetTab, 'assetSymbol');
    if (!currentSymbol) {
        yield put(assetTabsSlice.actions.initActiveTabAction(assetTabs[assetTabs.length - 1]));
        yield put(assetTabsSlice.actions.setIsInitializedAction(true));
        return;
    }
    yield call(handleAddTabSaga, currentSymbol);
    yield apply(searchParams, 'delete', ['asset']);
    appHistory.replace({
        search: searchParams.toString(),
        pathname: location.pathname,
        state: location.state,
    });
    yield put(assetTabsSlice.actions.setIsInitializedAction(true));
}

function* handleResetInitialSagaWorker() {
    yield apply(localStorage, 'removeItem', [STORAGE_KEY_ACTIVE_TAB]);
}

export function* assetTabsSagas() {
    yield takeEvery(assetTabsSlice.actions.initAddTabAction, handleAddTabSagaWorker);
    yield takeEvery(assetTabsSlice.actions.initDeleteTabAction, deleteTabSagaWorker);
    yield takeEvery(assetTabsSlice.actions.initAssetTabsAction, initAssetTabsWorker);
    yield takeEvery(assetTabsSlice.actions.initActiveTabAction, setActiveTabSagaWorker);
    yield takeEvery(assetTabsSlice.actions.resetInitialAction, handleResetInitialSagaWorker);
}